import { useEffect, useRef, useState, type ReactNode } from 'react';

import { api, ApiError } from '../../api/client';
import { consumeAuthReturn } from '../../app/AuthGuard';
import { hashQuery, loadPendingYandexRegistration, yandexOAuthErrorMessage } from '../../features/auth/yandex';
import { loadPendingVkRegistration, vkOAuthErrorMessage } from '../../features/auth/vk';
import { SocialAuthButtons } from '../../features/auth/SocialAuthButtons';
import { loadPendingOidc, oidcErrorMessage } from '../../features/auth/oidc';
import { useRegistrationDraft, type RegistrationDraft, type RegistrationRole } from '../../features/registration/model';
import { useFigmaBreakpoint, type FigmaBreakpoint } from '../../figma/useFigmaBreakpoint';
import { useAuth, type SessionUser } from '../../state/auth';
import { Button, Footer, Header, Input, InternationalPhoneInput, Tile, WizardButton } from '../../ui';
import { RegistrationEmailStep } from '../../features/registration/ui/RegistrationEmailStep';
import { RegistrationTypeStep } from '../../features/registration/ui/RegistrationTypeStep';
import { RegistrationSmsStep } from '../../features/registration/ui/RegistrationSmsStep';
import { RegistrationPasswordStep } from '../../features/registration/ui/RegistrationPasswordStep';
import type { ScreenId } from '../../app/screenRoutes';
import '../RequestWizard/request-wizard.css';
import './standalone-auth.css';

type StandaloneAuthScreenId = Extract<
  ScreenId,
  | 'login'
  | 'forgot-password'
  | 'forgot-password-sent'
  | 'reset-password'
  | 'reset-password-success'
  | 'email-confirm'
  | 'registration-role'
  | 'customer-rega-email'
  | 'customer-rega-type'
  | 'customer-rega-contact'
  | 'customer-rega-contact-company'
  | 'customer-rega-sent'
  | 'customer-rega-password'
  | 'customer-rega-success'
>;

const SCREEN_NODES: Record<StandaloneAuthScreenId, Partial<Record<FigmaBreakpoint, string>>> = {
  login: { '1920': '2824:51828', '1440': '2824:51975' },
  'forgot-password': { '1920': '2824:51828', '1440': '2824:51975' },
  'forgot-password-sent': { '1920': '2824:51828', '1440': '2824:51975' },
  'reset-password': { '1920': '2824:51828', '1440': '2824:51975' },
  'reset-password-success': { '1920': '2824:51828', '1440': '2824:51975' },
  'email-confirm': { '1920': '2824:51828', '1440': '2824:51975' },
  'registration-role': { '1920': '2787:53260', '1440': '2787:53209' },
  'customer-rega-email': { '1920': '2787:52761', '1440': '2787:52915' },
  'customer-rega-type': { '1920': '2787:52795', '1440': '2787:52949' },
  'customer-rega-contact': { '1920': '2787:52821', '1440': '2787:52975' },
  'customer-rega-contact-company': { '1920': '2787:52849', '1440': '2787:53003' },
  'customer-rega-sent': { '1920': '2787:52878', '1440': '2787:53032' },
  'customer-rega-password': { '1920': '2787:53122', '1440': '2787:53069' },
  'customer-rega-success': { '1920': '2787:53152', '1440': '2787:53099' },
};

const STEP_DETAILS: Partial<Record<StandaloneAuthScreenId, { step: number; label: string; previous?: StandaloneAuthScreenId }>> = {
  'customer-rega-email': { step: 1, label: 'email' },
  'customer-rega-type': { step: 2, label: 'тип аккаунта', previous: 'customer-rega-email' },
  'customer-rega-contact': { step: 3, label: 'контакты', previous: 'customer-rega-type' },
  'customer-rega-contact-company': { step: 3, label: 'контакты', previous: 'customer-rega-type' },
  'customer-rega-sent': { step: 4, label: 'подтверждение телефона', previous: 'customer-rega-contact' },
  'customer-rega-password': { step: 5, label: 'пароль', previous: 'customer-rega-sent' },
  'customer-rega-success': { step: 6, label: 'готово', previous: 'customer-rega-password' },
};

function navigate(screen: ScreenId) {
  window.location.hash = `#${screen}`;
}

function errorMessage(error: unknown): string {
  let msg = '';
  if (error instanceof ApiError && error.payload && typeof error.payload === 'object') {
    const first = Object.values(error.payload as Record<string, unknown>)[0];
    if (Array.isArray(first) && first.length) msg = String(first[0]);
    else if (typeof first === 'string') msg = first;
  }
  if (!msg) {
    msg = error instanceof Error ? error.message : 'Не удалось выполнить действие';
  }
  if (/invalid email or password/i.test(msg)) return 'Неверный email или пароль';
  return msg.replace(/\.+$/, '');
}

function AuthProgress({ label, previous, step }: { label: string; previous?: StandaloneAuthScreenId; step: number }) {
  return (
    <div className={`request-wizard__progress standalone-auth__progress${previous ? '' : ' standalone-auth__progress--first'}`} data-figma-node="124:32118">
      <div className="request-wizard__progress-bars" data-figma-variant={`Step=${step}`}>
        {Array.from({ length: 6 }, (_, index) => <i className={index < step ? 'is-active' : ''} key={index} />)}
      </div>
      <div className="request-wizard__progress-meta">
        {previous ? (
          <Button kind="tertiary" size="medium" icon="left" onClick={() => navigate(previous)}>Назад</Button>
        ) : <span aria-hidden="true" />}
        <span className="standalone-auth__progress-label">
          <b>{`Регистрация заказчика (${step}/6)`}</b><i aria-hidden="true" /><span>{label}</span>
        </span>
      </div>
    </div>
  );
}

function AuthShell({ children, screen }: { children: ReactNode; screen: StandaloneAuthScreenId }) {
  const breakpoint = useFigmaBreakpoint();
  const node = SCREEN_NODES[screen][breakpoint]
    ?? SCREEN_NODES[screen]['1440']
    ?? SCREEN_NODES[screen]['1920'];
  const step = STEP_DETAILS[screen];

  return (
    <section className={`request-wizard standalone-auth standalone-auth--${screen}`} data-screen={screen} data-figma-node={node} data-figma-variant={breakpoint}>
      <div className="request-wizard__stage standalone-auth__stage">
        <Header />
        <div className="request-wizard__layout standalone-auth__layout">
          <div className="request-wizard__image standalone-auth__image" role="img" aria-label="Дорога к месту назначения" data-figma-node="2787:52765" />
          <main className="request-wizard__form-shell standalone-auth__form-shell">
            {step && <AuthProgress label={step.label} previous={step.previous} step={step.step} />}
            {children}
          </main>
        </div>
      </div>
      <Footer legalDetails={{
        legalName: 'ООО/ИП “ХХХ”',
        inn: '123456789012',
        ogrnip: '12345678901234567',
      }} onNavigate={(action) => {
        if (action === 'logo') navigate('landing');
        if (action === 'customer-cabinet') navigate('my-requests');
        if (action === 'carrier-cabinet') navigate('carrier-dashboard');
        if (action === 'carrier-registration') navigate('carrier-rega-email');
      }} />
    </section>
  );
}

function FormError({ message }: { message: string }) {
  return message ? <p className="request-wizard__error standalone-auth__error" role="alert">{message.replace(/\.+$/, '')}</p> : null;
}

function LoginScreen({ draft, updateDraft }: { draft: RegistrationDraft; updateDraft: (patch: Partial<RegistrationDraft>) => void }) {
  const { setAuthenticatedUser } = useAuth();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const query = hashQuery();
    const provider = query.get('oauth_provider');
    if (provider === 'google' || provider === 'apple') {
      setError(oidcErrorMessage(provider, query.get('oauth_error')));
      if (query.get('oauth_error') === 'email_exists') {
        void loadPendingOidc(provider).then((pending) => {
          updateDraft({ email: pending.email, targetRole: pending.role });
        }).catch(() => undefined);
      }
      return;
    }
    setError(query.get('oauth_provider') === 'vk'
      ? vkOAuthErrorMessage(query.get('oauth_error'))
      : yandexOAuthErrorMessage(query.get('oauth_error')));
    if (!query.get('oauth_provider') && query.get('oauth_error') === 'email_exists') {
      void loadPendingYandexRegistration().then((pending) => {
        updateDraft({ email: pending.email, targetRole: pending.role });
      }).catch(() => undefined);
    }
  }, []);

  const login = async () => {
    const email = draft.email.trim();
    if (!email) {
      setError('Введите email.');
      return;
    }
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
      setError('Введите корректный email.');
      return;
    }
    if (!password) {
      setError('Введите пароль.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const user = await api<SessionUser>('/api/auth/login/', { method: 'POST', body: { email, password } });
      const hasRole = draft.targetRole === 'customer' ? user.has_customer_profile : user.has_carrier_profile;
      if (!hasRole) throw new Error(draft.targetRole === 'customer' ? 'У аккаунта нет профиля заказчика.' : 'У аккаунта нет профиля перевозчика.');
      const activeUser = user.active_role === draft.targetRole
        ? user
        : await api<SessionUser>('/api/auth/active-role/', {
            method: 'POST',
            body: { active_role: draft.targetRole },
          });
      setAuthenticatedUser(activeUser);
      const fallback = draft.targetRole === 'customer' ? '#my-requests' : '#carrier-dashboard';
      window.location.hash = consumeAuthReturn(draft.targetRole, fallback);
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="standalone-auth__login" data-figma-node="2824:51981">
      <div className="standalone-auth__copy standalone-auth__copy--tall">
        <h1>Войти в LOVO</h1>
        <p>Выберите роль и введите данные аккаунта</p>
      </div>
      <div className="standalone-auth__role-tabs" data-figma-node="2824:51986">
        {(['customer', 'carrier'] as RegistrationRole[]).map((role) => (
          <button className={draft.targetRole === role ? 'is-active' : ''} key={role} onClick={() => updateDraft({ targetRole: role })} type="button">
            {role === 'customer' ? 'Заказчик' : 'Перевозчик'}
          </button>
        ))}
      </div>
      <div className="standalone-auth__login-inputs">
        <Input
          density="compact"
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="anna@example.com"
          value={draft.email}
          error={error && !error.includes('парол') && !error.includes('профил') ? error : undefined}
          onChange={(event) => {
            setError('');
            updateDraft({ email: event.target.value });
          }}
        />
        <Input
          density="compact"
          label="Пароль"
          type="password"
          autoComplete="current-password"
          placeholder="Пароль"
          value={password}
          error={error && (error.includes('парол') || error.includes('профил')) ? error : undefined}
          onChange={(event) => {
            setError('');
            setPassword(event.target.value);
          }}
        />
      </div>
      <div className="standalone-auth__login-actions">
        <WizardButton disabled={busy} onClick={() => void login()}>{busy ? 'Входим…' : 'Войти'}</WizardButton>
        <Button kind="tertiary" size="medium" onClick={() => navigate('forgot-password')}>Забыли пароль?</Button>
      </div>
      <div className="standalone-auth__create-account">
        <span>Нет аккаунта?</span>
        <WizardButton kind="secondary" onClick={() => navigate('registration-role')}>Создать аккаунт</WizardButton>
      </div>
      <div className="standalone-auth__social-block">
        <div className="request-wizard__divider"><span>или войти через</span></div>
        <SocialAuthButtons role={draft.targetRole} flow="login" />
        <p>Продолжая, вы соглашаетесь с условиями оферты и обработкой персональных данных</p>
      </div>
    </div>
  );
}

function ForgotPasswordScreen({ draft, updateDraft }: { draft: RegistrationDraft; updateDraft: (patch: Partial<RegistrationDraft>) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    const email = draft.email.trim();
    if (!email) return setError('Введите email.');
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) return setError('Введите корректный email.');
    setBusy(true);
    setError('');
    try {
      await api('/api/auth/password/reset/request/', { method: 'POST', body: { email } });
      navigate('forgot-password-sent');
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="standalone-auth__account-action" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
      <div className="standalone-auth__copy standalone-auth__copy--tall">
        <h1>Восстановить пароль</h1>
        <p>Введите email аккаунта. Мы отправим ссылку для создания нового пароля.</p>
      </div>
      <div className="standalone-auth__account-action-fields">
        <Input
          density="compact"
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="anna@example.com"
          value={draft.email}
          error={error}
          onChange={(event) => { setError(''); updateDraft({ email: event.target.value }); }}
        />
      </div>
      <div className="standalone-auth__account-action-buttons">
        <WizardButton type="submit" disabled={busy}>{busy ? 'Отправляем…' : 'Получить ссылку'}</WizardButton>
        <Button kind="tertiary" size="medium" onClick={() => navigate('login')}>Вернуться ко входу</Button>
      </div>
    </form>
  );
}

function ForgotPasswordSentScreen({ draft }: { draft: RegistrationDraft }) {
  return (
    <div className="standalone-auth__account-action standalone-auth__account-action--result">
      <div className="standalone-auth__copy standalone-auth__copy--tall">
        <h1>Проверьте почту</h1>
        <p>Если аккаунт {draft.email || 'с таким email'} существует, письмо со ссылкой уже отправлено.</p>
      </div>
      <div className="request-wizard__success-box standalone-auth__account-action-notice">
        <strong>Ссылка действует 30 минут</strong>
        <span>Не нашли письмо? Проверьте папку «Спам» или запросите новую ссылку.</span>
      </div>
      <div className="standalone-auth__account-action-buttons">
        <WizardButton onClick={() => navigate('login')}>Вернуться ко входу</WizardButton>
        <WizardButton kind="secondary" onClick={() => navigate('forgot-password')}>Отправить ещё раз</WizardButton>
      </div>
    </div>
  );
}

function ResetPasswordScreen() {
  const token = hashQuery().get('token') ?? '';
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [validation, setValidation] = useState<'checking' | 'valid' | 'invalid'>('checking');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    api<{ valid: boolean }>(`/api/auth/password/reset/validate/?${new URLSearchParams({ token })}`)
      .then((result) => { if (active) setValidation(result.valid ? 'valid' : 'invalid'); })
      .catch(() => { if (active) setValidation('invalid'); });
    return () => { active = false; };
  }, [token]);

  const submit = async () => {
    if (password.length < 8 || !/[A-ZА-Я]/.test(password) || !/\d/.test(password)) return setError('Пароль: минимум 8 символов, одна заглавная буква и одна цифра.');
    if (password !== repeat) return setError('Пароли не совпадают.');
    setBusy(true);
    setError('');
    try {
      await api('/api/auth/password/reset/confirm/', { method: 'POST', body: { token, new_password: password } });
      navigate('reset-password-success');
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  if (validation === 'checking') {
    return <AccountActionResult title="Проверяем ссылку" description="Это займёт несколько секунд." primaryLabel={null} />;
  }
  if (validation === 'invalid') {
    return (
      <AccountActionResult
        title="Ссылка не работает"
        description="Срок действия ссылки истёк или она уже была использована. Запросите новую."
        primaryLabel="Получить новую ссылку"
        primaryScreen="forgot-password"
      />
    );
  }
  return (
    <form className="standalone-auth__account-action" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
      <div className="standalone-auth__copy standalone-auth__copy--tall">
        <h1>Новый пароль</h1>
        <p>Придумайте новый пароль для входа в LOVO.</p>
      </div>
      <div className="standalone-auth__account-action-fields standalone-auth__account-action-fields--password">
        <Input density="compact" label="Новый пароль" type="password" autoComplete="new-password" placeholder="Минимум 8 символов" value={password} onChange={(event) => { setError(''); setPassword(event.target.value); }} />
        <Input density="compact" label="Повторите пароль" type="password" autoComplete="new-password" placeholder="Повторите пароль" value={repeat} error={error} onChange={(event) => { setError(''); setRepeat(event.target.value); }} />
      </div>
      <div className="standalone-auth__account-action-buttons">
        <WizardButton type="submit" disabled={busy}>{busy ? 'Сохраняем…' : 'Сохранить новый пароль'}</WizardButton>
        <Button kind="tertiary" size="medium" onClick={() => navigate('login')}>Вернуться ко входу</Button>
      </div>
    </form>
  );
}

function AccountActionResult({ title, description, primaryLabel = 'Войти', primaryScreen = 'login' }: { title: string; description: string; primaryLabel?: string | null; primaryScreen?: ScreenId }) {
  return (
    <div className="standalone-auth__account-action standalone-auth__account-action--result">
      <div className="standalone-auth__copy standalone-auth__copy--tall">
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {primaryLabel ? (
        <div className="standalone-auth__account-action-buttons">
          <Button size="medium" onClick={() => navigate(primaryScreen)}>{primaryLabel}</Button>
          {primaryScreen !== 'login' ? <Button kind="tertiary" size="medium" onClick={() => navigate('login')}>Вернуться ко входу</Button> : null}
        </div>
      ) : null}
    </div>
  );
}

function EmailConfirmScreen() {
  const { refresh, status, user } = useAuth();
  const token = hashQuery().get('token') ?? '';
  const started = useRef(false);
  const [result, setResult] = useState<'checking' | 'success' | 'invalid'>('checking');

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    api('/api/auth/email/verify/', { method: 'POST', body: { token } })
      .then(async () => {
        if (status === 'authenticated') await refresh();
        setResult('success');
      })
      .catch(() => setResult('invalid'));
  }, [refresh, status, token]);

  if (result === 'checking') return <AccountActionResult title="Подтверждаем email" description="Проверяем ссылку. Это займёт несколько секунд." primaryLabel={null} />;
  if (result === 'invalid') {
    return <AccountActionResult title="Ссылка не работает" description="Срок действия ссылки истёк или она уже была использована." primaryLabel="Вернуться ко входу" />;
  }
  const target: ScreenId = user?.active_role === 'carrier' ? 'carrier-dashboard' : user?.active_role === 'customer' ? 'my-requests' : 'login';
  return <AccountActionResult title="Email подтверждён" description="Адрес подтверждён. Теперь все почтовые уведомления LOVO будут доступны." primaryLabel={target === 'login' ? 'Войти' : 'Перейти в кабинет'} primaryScreen={target} />;
}

function RegistrationRoleScreen({ draft, updateDraft }: { draft: RegistrationDraft; updateDraft: (patch: Partial<RegistrationDraft>) => void }) {
  return (
    <div className="standalone-auth__role-choice" data-figma-node="2787:53216">
      <div className="standalone-auth__copy">
        <h1>Заказчик или перевозчик?</h1>
        <p>Выберите роль, чтобы продолжить</p>
      </div>
      <div className="standalone-auth__role-choice-tiles">
        <Tile active={draft.targetRole === 'customer'} title="Я заказчик" description="Ищу перевозчика для питомца" onClick={() => updateDraft({ targetRole: 'customer' })} />
        <Tile active={draft.targetRole === 'carrier'} title="Я перевозчик" description="Выполняю рейсы и беру заказы" onClick={() => updateDraft({ targetRole: 'carrier' })} />
      </div>
      <Button onClick={() => navigate(draft.targetRole === 'customer' ? 'customer-rega-email' : 'carrier-rega-email')}>Продолжить</Button>
    </div>
  );
}

function CustomerEmailScreen({ draft, updateDraft }: { draft: RegistrationDraft; updateDraft: (patch: Partial<RegistrationDraft>) => void }) {
  return (
    <RegistrationEmailStep
      role="customer"
      draft={draft}
      updateDraft={updateDraft}
      figmaNode="2787:52928"
      onExistingUser={() => navigate('login')}
      onNewUser={() => navigate('customer-rega-type')}
      onSwitchRole={() => navigate('carrier-rega-email')}
    />
  );
}

function CustomerTypeScreen({ draft, updateDraft }: { draft: RegistrationDraft; updateDraft: (patch: Partial<RegistrationDraft>) => void }) {
  return (
    <RegistrationTypeStep
      role="customer"
      draft={draft}
      updateDraft={updateDraft}
      figmaNode="2787:52963"
      onSelectCustomerType={(type) => navigate(type === 'company' ? 'customer-rega-contact-company' : 'customer-rega-contact')}
      onSelectCarrierType={() => {}}
    />
  );
}

function CustomerContactScreen({ company, draft, updateDraft }: { company: boolean; draft: RegistrationDraft; updateDraft: (patch: Partial<RegistrationDraft>) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const sendCode = async () => {
    const identityMissing = company ? !draft.companyName.trim() || !draft.contactName.trim() : !draft.firstName.trim();
    if (identityMissing || !draft.phone.trim()) return setError('Заполните контактные данные и телефон.');
    setBusy(true);
    setError('');
    try {
      const result = await api<{ debug_code?: string }>('/api/auth/sms/send/', { method: 'POST', body: { phone: draft.phone.trim(), purpose: 'register' } });
      updateDraft({ smsCode: result.debug_code ?? '', debugSmsCode: result.debug_code ?? '' });
      navigate('customer-rega-sent');
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`standalone-auth__registration-form standalone-auth__registration-form--contact${company ? ' standalone-auth__registration-form--company' : ''}`} data-figma-node={company ? '2787:53017' : '2787:52989'}>
      <div className={`standalone-auth__copy${company ? '' : ' standalone-auth__copy--tall'}`}>
        <h1>{company ? 'Расскажите о компании' : 'Расскажите о себе'}</h1>
        <p>{company ? 'Компания · одно дополнительное поле — название организации' : 'Частное лицо · имя\u00a0нужно перевозчику для\u00a0связи, телефон\u00a0— для\u00a0оперативных уведомлений'}</p>
      </div>
      <div className="standalone-auth__contact-inputs">
        {company ? (
          <>
            <Input density="compact" label="Название компании" placeholder="Например, Питомник «Бэсти»" value={draft.companyName} onChange={(event) => updateDraft({ companyName: event.target.value })} />
            <Input density="compact" label="Ваше имя (контактного лица)" placeholder="Анна" value={draft.contactName} onChange={(event) => updateDraft({ contactName: event.target.value })} />
          </>
        ) : <Input density="compact" label="Ваше имя" placeholder="Анна" value={draft.firstName} onChange={(event) => updateDraft({ firstName: event.target.value })} />}
        <div className="standalone-auth__phone-field">
          <InternationalPhoneInput density="compact" label="Телефон" value={draft.phone} onChange={(phone) => updateDraft({ phone })} />
          <p>Передаём перевозчику только после того, как вы примете отклик</p>
        </div>
      </div>
      <div className="standalone-auth__contact-actions">
        <WizardButton disabled={busy} onClick={() => void sendCode()}>{busy ? 'Отправляем…' : 'Создать аккаунт'}</WizardButton>
        <p>{company ? 'Реквизиты и документы организации можно загрузить позже в профиле' : 'Документы и доп. данные можно заполнить позже в профиле'}</p>
      </div>
      <FormError message={error} />
    </div>
  );
}

function CodeInputs({ code, onChange }: { code: string; onChange: (value: string) => void }) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  return (
    <div className="request-wizard__code-row standalone-auth__code-row">
      {Array.from({ length: 6 }, (_, index) => (
        <input
          aria-label={`Цифра ${index + 1}`}
          className={code[index] ? 'is-filled' : ''}
          inputMode="numeric"
          key={index}
          maxLength={1}
          ref={(node) => { refs.current[index] = node; }}
          value={code[index] ?? ''}
          onChange={(event) => {
            const digit = event.target.value.replace(/\D/g, '').slice(-1);
            const chars = code.padEnd(6, ' ').split('');
            chars[index] = digit || ' ';
            onChange(chars.join('').trimEnd());
            if (digit && index < 5) refs.current[index + 1]?.focus();
          }}
        />
      ))}
    </div>
  );
}

function CustomerSmsScreen({ draft, updateDraft }: { draft: RegistrationDraft; updateDraft: (patch: Partial<RegistrationDraft>) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resendSeconds, setResendSeconds] = useState(42);

  useEffect(() => {
    if (resendSeconds <= 0) return undefined;
    const timer = window.setTimeout(() => setResendSeconds((current) => current - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendSeconds]);

  const verify = async () => {
    if (draft.smsCode.replace(/\s/g, '').length !== 6) return setError('Введите шестизначный код.');
    setBusy(true);
    setError('');
    try {
      await api('/api/auth/sms/verify/', { method: 'POST', body: { phone: draft.phone, purpose: 'register', code: draft.smsCode } });
      navigate('customer-rega-password');
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await api<{ debug_code?: string }>('/api/auth/sms/send/', { method: 'POST', body: { phone: draft.phone, purpose: 'register' } });
      updateDraft({ smsCode: result.debug_code ?? '', debugSmsCode: result.debug_code ?? '' });
      setResendSeconds(42);
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="standalone-auth__registration-form standalone-auth__registration-form--sms" data-figma-node="2787:53046">
      <div className="standalone-auth__copy standalone-auth__copy--tall">
        <h1>Введите код из SMS</h1>
        <p>Отправили на {draft.phone}. Подтверждаем телефон сейчас — email подтвердите потом в профиле.</p>
      </div>
      <CodeInputs code={draft.smsCode} onChange={(smsCode) => updateDraft({ smsCode })} />
      <div className="request-wizard__sms-actions standalone-auth__sms-actions">
        <WizardButton disabled={busy} onClick={() => void verify()}>{busy ? 'Проверяем…' : 'Подтвердить'}</WizardButton>
        <Button kind="tertiary" size="medium" onClick={() => {
          if (!busy && resendSeconds === 0) void resend();
        }}>
          {`Не пришёл код? Отправить снова${resendSeconds > 0 ? ` через 0:${String(resendSeconds).padStart(2, '0')}` : ''}`}
        </Button>
        <Button kind="tertiary" size="medium" onClick={() => navigate(draft.accountType === 'company' ? 'customer-rega-contact-company' : 'customer-rega-contact')}>Сменить номер телефона</Button>
      </div>
      <FormError message={error} />
    </div>
  );
}

function CustomerPasswordScreen({ draft }: { draft: RegistrationDraft }) {
  const { setAuthenticatedUser } = useAuth();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const register = async () => {
    if (password.length < 8 || !/[A-ZА-Я]/.test(password) || !/\d/.test(password)) return setError('Пароль: минимум 8 символов, одна заглавная буква и одна цифра.');
    if (password !== repeat) return setError('Пароли не совпадают.');
    setBusy(true);
    setError('');
    try {
      const user = await api<SessionUser>('/api/auth/register/', {
        method: 'POST',
        body: {
          role: 'customer', email: draft.email, password, phone: draft.phone, sms_code: draft.smsCode,
          account_type: draft.accountType, first_name: draft.firstName, last_name: draft.lastName,
          company_name: draft.companyName, contact_name: draft.contactName,
        },
      });
      setAuthenticatedUser(user);
      navigate('customer-rega-success');
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="standalone-auth__registration-form standalone-auth__registration-form--password"
      data-figma-node="2787:53083"
      onSubmit={(e) => {
        e.preventDefault();
        void register();
      }}
    >
      <div className="standalone-auth__copy">
        <h1>Придумайте пароль</h1>
        <p>Понадобится для входа с других устройств</p>
      </div>
      <div className="standalone-auth__password-content">
        <div className="request-wizard__success-box">
          <strong>✓ Телефон подтверждён: {draft.phone}</strong>
          <span>После создания аккаунта отправим подтверждение на {draft.email}</span>
        </div>
        <div className="request-wizard__password-fields">
          <Input density="compact" label="Пароль" type="password" autoComplete="new-password" placeholder="Минимум 8 символов" value={password} onChange={(event) => setPassword(event.target.value)} />
          <p>Латиница, цифры, минимум одна заглавная буква</p>
          <Input density="compact" label="Повторите пароль" type="password" autoComplete="new-password" placeholder="Повторите" value={repeat} onChange={(event) => setRepeat(event.target.value)} />
        </div>
      </div>
      <WizardButton type="submit" disabled={busy}>{busy ? 'Сохраняем…' : 'Сохранить'}</WizardButton>
      <FormError message={error} />
    </form>
  );
}

function CustomerSuccessScreen({ clearDraft }: { clearDraft: () => void }) {
  const leave = (screen: ScreenId) => {
    clearDraft();
    navigate(screen);
  };
  return (
    <div className="standalone-auth__registration-form standalone-auth__registration-form--success" data-figma-node="2787:53113">
      <div className="standalone-auth__copy standalone-auth__copy--tall">
        <h1>Готово! Добро пожаловать в LOVO</h1>
        <p>Аккаунт создан. Мы отправили письмо для подтверждения email — можно продолжать работу.</p>
      </div>
      <div className="standalone-auth__success-actions">
        <Button onClick={() => leave('new-request-step-1')}>Оформить заявку</Button>
        <Button kind="secondary" onClick={() => leave('landing')}>На главную</Button>
      </div>
    </div>
  );
}

export function StandaloneAuthScreen({ screen }: { screen: StandaloneAuthScreenId }) {
  const { clearDraft, draft, updateDraft } = useRegistrationDraft();

  useEffect(() => {
    const provider = hashQuery().get('oauth');
    if (provider !== 'yandex' && provider !== 'vk' && provider !== 'google' && provider !== 'apple') return;
    const loadPending = provider === 'google' || provider === 'apple' ? () => loadPendingOidc(provider) : provider === 'vk' ? loadPendingVkRegistration : loadPendingYandexRegistration;
    void loadPending()
      .then((pending) => updateDraft({
        targetRole: pending.role,
        email: pending.email,
        firstName: pending.first_name,
        lastName: pending.last_name,
      }))
      .catch(() => undefined);
  }, [screen, updateDraft]);
  let content: ReactNode = null;

  if (screen === 'login') content = <LoginScreen draft={draft} updateDraft={updateDraft} />;
  if (screen === 'forgot-password') content = <ForgotPasswordScreen draft={draft} updateDraft={updateDraft} />;
  if (screen === 'forgot-password-sent') content = <ForgotPasswordSentScreen draft={draft} />;
  if (screen === 'reset-password') content = <ResetPasswordScreen />;
  if (screen === 'reset-password-success') content = <AccountActionResult title="Пароль изменён" description="Новый пароль сохранён. Теперь используйте его для входа в LOVO." />;
  if (screen === 'email-confirm') content = <EmailConfirmScreen />;
  if (screen === 'registration-role') content = <RegistrationRoleScreen draft={draft} updateDraft={updateDraft} />;
  if (screen === 'customer-rega-email') content = <CustomerEmailScreen draft={draft} updateDraft={updateDraft} />;
  if (screen === 'customer-rega-type') content = <CustomerTypeScreen draft={draft} updateDraft={updateDraft} />;
  if (screen === 'customer-rega-contact') content = <CustomerContactScreen company={false} draft={draft} updateDraft={updateDraft} />;
  if (screen === 'customer-rega-contact-company') content = <CustomerContactScreen company draft={draft} updateDraft={updateDraft} />;
  if (screen === 'customer-rega-sent') {
    content = (
      <RegistrationSmsStep
        draft={draft}
        role="customer"
        updateDraft={updateDraft}
        onSuccess={() => navigate('customer-rega-password')}
        onChangePhone={() =>
          navigate(
            draft.accountType === 'company'
              ? 'customer-rega-contact-company'
              : 'customer-rega-contact',
          )
        }
      />
    );
  }
  if (screen === 'customer-rega-password') {
    content = (
      <RegistrationPasswordStep
        draft={draft}
        role="customer"
        onSuccess={() => navigate('customer-rega-success')}
      />
    );
  }
  if (screen === 'customer-rega-success') content = <CustomerSuccessScreen clearDraft={clearDraft} />;

  return <AuthShell screen={screen}>{content}</AuthShell>;
}
